import React, { useState, useEffect, useRef } from 'react';
import { Modal, Box, Typography, Button, TextField, FormControl, Chip, Autocomplete, ListItemText, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV2';
import { LocalizationProvider, DatePicker, TimePicker } from '@mui/x-date-pickers';
import axios from 'axios';
import { APIURL } from '../../../config';
import moment from 'moment';
import { convertTime } from 'src/utils/time';

function EditTaskSessionModal(props) {
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeesToRemove, setEmployeesToRemove] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [scheduleName, setScheduleName] = useState('');
  const [notes, setNotes] = useState('');
  const [sessionNotes, setSessionNotes] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({
    selectedEmployee: false,
    startTime: false,
    endTime: false
  });
  const initialDataRef = useRef(null);
  const initialAssignedEmployeeIdsRef = useRef([]);
  const isCreatingTaskRef = useRef(false);
  const isSubmittingRef = useRef(false);

  // const isAdmin = props.isAdmin; // Using passed prop to check if the user is an admin

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    // Prevent duplicate submissions from race conditions
    if (isSubmittingRef.current) {
      console.log('Submission already in progress, skipping');
      return;
    }
    
    if (!validate()) {
      return;
    }
    
    isSubmittingRef.current = true;

    if (!props.schedule) {
      props.onError('Session details are unavailable. Close the modal and try again.');
      isSubmittingRef.current = false;
      return;
    }

    const scheduleId = props.schedule?.schedule_id;
    const snapshot = initialDataRef.current || {
      startDate: props.schedule?.date ? props.schedule.date.substring(0, 10) : '',
      startTime: props.schedule?.start_time ? moment(props.schedule.start_time).format('HH:mm') : '',
      endTime: props.schedule?.end_time ? moment(props.schedule.end_time).format('HH:mm') : '',
      sessionNotes: props.schedule?.notes || '',
      employeeIds: Array.isArray(props.schedule?.employee_ids) ? [...props.schedule.employee_ids] : [],
    };

    const baseDateString = snapshot.startDate || (props.schedule?.date ? props.schedule.date.substring(0, 10) : moment().format('YYYY-MM-DD'));
    const sessionDateString = startDate ? moment(startDate).format('YYYY-MM-DD') : baseDateString;
    const startMoment = startTime
      ? moment(startTime)
      : (snapshot.startTime && baseDateString ? moment(`${baseDateString} ${snapshot.startTime}`) : moment(props.schedule?.start_time));
    const endMoment = endTime
      ? moment(endTime)
      : (snapshot.endTime && baseDateString ? moment(`${baseDateString} ${snapshot.endTime}`) : moment(props.schedule?.end_time));

    const startTimeFormatted = startMoment.format('HH:mm');
    const endTimeFormatted = endMoment.format('HH:mm');

    const schedule = {
      date: sessionDateString,
      start_time: moment(`${sessionDateString} ${startTimeFormatted}`).utc().format('YYYY-MM-DD HH:mm:00'),
      end_time: moment(`${sessionDateString} ${endTimeFormatted}`).utc().format('YYYY-MM-DD HH:mm:00'),
      notes: sessionNotes,
      resource_id: props.schedule.resource_id,
      max_capacity: props.schedule.max_capacity,
      schedule_name: scheduleName,
      task_type: props.schedule.task_type,
    };

    // Compute employee additions and removals by comparing initial vs current state
    const initialEmployeeIds = new Set(initialAssignedEmployeeIdsRef.current);
    const employeeToAdd = selectedEmployee && !initialEmployeeIds.has(selectedEmployee.id) ? selectedEmployee : null;
    const employeesToDelete = employeesToRemove.filter(emp => initialEmployeeIds.has(emp.id));

    if (employeeToAdd) {
      try {
        await axios.post(APIURL + '/master_schedule/employee/' + employeeToAdd.id, { schedule_id: scheduleId });
        await createTask();
        props.onSuccess('Employee assigned to session successfully');
      } catch (error) {
        props.onError('An error occurred while assigning the employee to the session');
        isSubmittingRef.current = false;
        return;
      }
    }

    if (employeesToDelete.length > 0) {
      try {
        for (const employee of employeesToDelete) {
          await axios.delete(APIURL + '/master_schedule/employee/' + employee.id, { data: { schedule_id: scheduleId } });
        }
        props.onSuccess('Employee removed from session successfully');
      } catch (error) {
        props.onError('An error occurred while removing the employee from the session');
        isSubmittingRef.current = false;
        return;
      }
    }

    const startChanged = startTimeFormatted !== snapshot.startTime;
    const endChanged = endTimeFormatted !== snapshot.endTime;
    const dateChanged = sessionDateString !== snapshot.startDate;
    const notesChanged = sessionNotes !== snapshot.sessionNotes;
    const nameChanged = scheduleName !== (snapshot.scheduleName ?? '');

    if (startChanged || endChanged || dateChanged || notesChanged || nameChanged) {
      try {
        await axios.put(APIURL + '/master_schedule/' + scheduleId, schedule);
        props.onSuccess('Schedule updated successfully');
      } catch (error) {
        if (error.response) {
          props.onError(error.response.data.message);
        } else {
          props.onError('An error occurred while updating the schedule');
        }
        isSubmittingRef.current = false;
        return;
      }
    }

    isSubmittingRef.current = false;
    props.reloadSessionData();
    props.reloadScheduleData();
    props.onClose();
  };

  const handleDeleteEmployee = (employee) => {
    setEmployeesToRemove(prev => {
      if (prev.some(item => item.id === employee.id)) {
        return prev;
      }
      return [...prev, employee];
    });
  };

  const validate = () => {
    const snapshot = initialDataRef.current || {
      startDate: props.schedule?.date ? props.schedule.date.substring(0, 10) : '',
      startTime: props.schedule?.start_time ? moment(props.schedule.start_time).format('HH:mm') : '',
      endTime: props.schedule?.end_time ? moment(props.schedule.end_time).format('HH:mm') : '',
      sessionNotes: props.schedule?.notes || '',
      employeeIds: Array.isArray(props.schedule?.employee_ids) ? [...props.schedule.employee_ids] : [],
    };

    const baseDateString = snapshot.startDate || (props.schedule?.date ? props.schedule.date.substring(0, 10) : moment().format('YYYY-MM-DD'));
    const sessionDateString = startDate ? moment(startDate).format('YYYY-MM-DD') : baseDateString;
    const startMoment = startTime ? moment(startTime) : null;
    const endMoment = endTime ? moment(endTime) : null;

    const startError = !startMoment || (endMoment && !startMoment.isBefore(endMoment));
    const endError = !endMoment || (startMoment && !endMoment.isAfter(startMoment));

    const startChanged = startMoment ? startMoment.format('HH:mm') !== snapshot.startTime : false;
    const endChanged = endMoment ? endMoment.format('HH:mm') !== snapshot.endTime : false;
    const dateChanged = sessionDateString !== snapshot.startDate;
    const notesChanged = sessionNotes !== snapshot.sessionNotes;
    const nameChanged = scheduleName !== (snapshot.scheduleName ?? '');
    const employeeAdded = selectedEmployee && !snapshot.employeeIds.includes(selectedEmployee.id);
    const employeeRemoved = employeesToRemove.length > 0;

    const hasChanges = startChanged || endChanged || dateChanged || notesChanged || nameChanged || employeeAdded || employeeRemoved;

    const errors = {
      selectedEmployee: !hasChanges,
      startTime: startError,
      endTime: endError,
    };

    setErrors(errors);
    setSubmitted(true);
    return !Object.values(errors).some(err => err);
  };

  const unassginedEmployees = () => {
    const removalIds = new Set(employeesToRemove.map(employee => employee.id));
    if (!props.schedule?.employee_ids) {
      return props.employees;
    }
    return props.employees.filter(employee => !props.schedule.employee_ids.includes(employee.id) || removalIds.has(employee.id));
  };

  const assginedEmployees = () => {
    if (!props.schedule?.employee_ids) {
      return [];
    }
    const removalIds = new Set(employeesToRemove.map(employee => employee.id));
    return props.employees.filter(employee => props.schedule.employee_ids.includes(employee.id) && !removalIds.has(employee.id));
  };

  useEffect(() => {
    if (!props.open) {
      setSubmitted(false);
      setErrors({
        selectedEmployee: false,
        startTime: false,
        endTime: false
      });
      setSelectedEmployee(null);
      setEmployeesToRemove([]);
      setNotes('');
      setSessionNotes('');
      setStartDate(null);
      setStartTime(null);
      setEndTime(null);
      isCreatingTaskRef.current = false;
      isSubmittingRef.current = false;
      return;
    }

    if (!props.schedule) {
      return;
    }

    const scheduleDateString = props.schedule.date ? props.schedule.date.substring(0, 10) : '';
    const scheduleStart = props.schedule.start_time ? moment(props.schedule.start_time).toDate() : null;
    const scheduleEnd = props.schedule.end_time ? moment(props.schedule.end_time).toDate() : null;

    setSubmitted(false);
    setErrors({
      selectedEmployee: false,
      startTime: false,
      endTime: false
    });
    setSelectedEmployee(null);
    setEmployeesToRemove([]);
    setNotes('');
    setSessionNotes(props.schedule.notes || '');
    setScheduleName(props.schedule.schedule_name || '');
    setStartDate(scheduleDateString ? moment(scheduleDateString).toDate() : null);
    setStartTime(scheduleStart);
    setEndTime(scheduleEnd);

    initialDataRef.current = {
      startDate: scheduleDateString,
      startTime: props.schedule.start_time ? moment(props.schedule.start_time).format('HH:mm') : '',
      endTime: props.schedule.end_time ? moment(props.schedule.end_time).format('HH:mm') : '',
      sessionNotes: props.schedule.notes || '',
      scheduleName: props.schedule.schedule_name || '',
      employeeIds: Array.isArray(props.schedule.employee_ids) ? [...props.schedule.employee_ids] : [],
    };

    // Capture initial assigned employee IDs for diff calculation on submit
    initialAssignedEmployeeIdsRef.current = Array.isArray(props.schedule.employee_ids) ? [...props.schedule.employee_ids] : [];
  }, [props.open, props.schedule]);

  const createTask = async () => {
    // Prevent duplicate task creation from race conditions
    if (isCreatingTaskRef.current) {
      console.log('Task creation already in progress, skipping');
      return;
    }
    
    try {
        isCreatingTaskRef.current = true;
        
        const customerSession = props.sessions.filter(session => session.schedule_id === props.schedule.schedule_id)[0];
        
        // Check if task already exists for this session - prevent duplicate task creation
        if (customerSession?.task_id) {
          console.log('Task already exists for this session, skipping task creation');
          isCreatingTaskRef.current = false;
          return;
        }
        
        const projData = await axios.get(APIURL + '/project', { params: { cus_id: customerSession.cus_id } });            
        const proj = projData.data.filter(project => project.pro_is_active === true)[0] || projData.data[0];
        const locs = await axios.get(APIURL + '/projlocations', { params: { project_id: proj.pro_id } });
        
        const startMoment = startTime ? moment(startTime) : moment(props.schedule.start_time);
        const endMoment = endTime ? moment(endTime) : moment(props.schedule.end_time);

        let data = {
            company_id: props.comp_id,
            tTitle: props.schedule.schedule_name,
            tDesc: 'Session Task',
            tProject: proj.pro_id,
            tPreReq: 'NULL',
            tType: props.schedule.task_type,
          tStrtDate: startMoment.format('YYYY-MM-DD HH:mm:ss'),
          tEndDate: endMoment.format('YYYY-MM-DD HH:mm:ss'),
          tStartTime: convertTime(startMoment, moment.tz.guess(), props.user.emp_timezone, 'YYYY-MM-DD HH:mm:ss') ?? startMoment.format('YYYY-MM-DD HH:mm:ss'),
          tEndTime: convertTime(endMoment, moment.tz.guess(), props.user.emp_timezone, 'YYYY-MM-DD HH:mm:ss') ?? endMoment.format('YYYY-MM-DD HH:mm:ss'),
            tLoc: locs.data[0].loc_id,
            tActive: 1,
            tAssTo: props.user.emp_id,
            tPriority: 'Low',
            tIsBillable: props.user.comp_settings.find(setting => setting.feature_id === 56) ? props.user.comp_settings.find(setting => setting.feature_id === 56).feature_value === 'true' : false,
            tNotes: notes || '',
            user_id: props.user.emp_id,
            session_id: customerSession.session_id, // Link task to session to prevent duplicates
        };
        let tasRes = await axios.post(APIURL + '/task', data);
    } catch (error) {
        console.error(error);
    } finally {
        isCreatingTaskRef.current = false;
    }
  };

  return (
    <Modal enforceFocus={false}
      open={props.open}
      onClose={props.onClose}
      aria-labelledby="assign-entity-modal-title"
      aria-describedby="assign-entity-modal-description"
    >
      <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 500, maxWidth: '90%', bgcolor: 'background.paper', boxShadow: 24, p: 4, borderRadius: 2, maxHeight: '90vh', overflowY: 'auto' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography id="assign-entity-modal-title" variant="h6" component="div" sx={{ mb: 1 }}>
            Edit Session
          </Typography>
          <IconButton
            aria-label="close"
            color="inherit"
            sx={{ p: 0.25 }}
            onClick={props.onClose}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <TextField
          label="Session Name"
          fullWidth
          value={scheduleName}
          onChange={(e) => setScheduleName(e.target.value)}
          inputProps={{ maxLength: 50 }}
          sx={{ mb: 1, mt: 0.5 }}
        />
        <Typography variant="subtitle2" component="div" sx={{ mb: 1 }}>
          {props.resources.find(resource => resource.resource_id === props.schedule?.resource_id)?.resource_name} {moment(props.schedule?.date).utc().format('MM/DD/YYYY')} {moment(props.schedule?.start_time).format('hh:mm A')} - {moment(props.schedule?.end_time).format('hh:mm A')}
        </Typography>
        <Typography variant="subtitle2" component="div" sx={{ mb: 1 }}>
            Assigned Employee:
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {assginedEmployees().map(employee => (
            <Chip key={employee.id} label={employee.employee_name} color="primary" sx={{ mb: .15}} onDelete={() => handleDeleteEmployee(employee)} />
            ))}
        </Box>
        {employeesToRemove.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {employeesToRemove.map(employee => (
              <Chip
                key={`remove-${employee.id}`}
                label={employee.employee_name}
                color="error"
                variant="outlined"
                onDelete={() => setEmployeesToRemove(prev => prev.filter(item => item.id !== employee.id))}
              />
            ))}
          </Box>
        )}
        <FormControl fullWidth margin="normal" sx={{ mt: 1, mb: 2 }}>
            <Autocomplete
            id="employee-select"
            options={props.user.permissions.ScheduleSessionForOtherEmployees ? unassginedEmployees() : unassginedEmployees().filter(employee => employee.id === props.user.emp_id)}
            getOptionLabel={(option) => option.employee_name}
            filterSelectedOptions
            disabled={assginedEmployees().length > 0}
            value={selectedEmployee}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            renderInput={(params) => (
                <TextField
                {...params}
                variant="outlined"
                label="Select Employee"
                placeholder="Employee"
                error={errors.selectedEmployee && submitted}
                />
            )}
            onChange={(event, newValue) => {
                if (!newValue) {
                  setSelectedEmployee(null);
                  return;
                }

                const wasMarkedForRemoval = employeesToRemove.some(employee => employee.id === newValue.id);
                if (wasMarkedForRemoval) {
                  setEmployeesToRemove(prev => prev.filter(employee => employee.id !== newValue.id));
                  setSelectedEmployee(null);
                  return;
                }

                const snapshot = initialDataRef.current;
                const alreadyAssigned = snapshot?.employeeIds?.includes(newValue.id) || (props.schedule?.employee_ids || []).includes(newValue.id);
                if (alreadyAssigned) {
                  setSelectedEmployee(null);
                  return;
                }

                setSelectedEmployee(newValue);
            }}
            renderOption={(props, option) => {
                return (
                <li {...props} key={option.id}>
                    <ListItemText primary={option.employee_name} />
                </li>
                )
            }}
            />
        </FormControl>
        <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
          <Box sx={{ display: 'flex ', flexDirection: 'row', gap: 1, mb: 1 }}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Date"
                value={startDate}
                sx={{ width: '36%' }}
                onChange={(newValue) => setStartDate(newValue)}
              />
              <TimePicker
                label="Start Time"
                value={startTime}
                slotProps={{ textField: { error: errors.startTime && submitted } }}
                sx={{ width: '32%' }}
                onChange={(newValue) => setStartTime(newValue)}
              />
              <TimePicker
                label="End Time"
                value={endTime}
                onChange={(newValue) => setEndTime(newValue)}
                slotProps={{ textField: { error: errors.endTime && submitted } }}
                sx={{ width: '32%' }}
              />
            </LocalizationProvider>
          </Box>
          <TextField
            margin="normal"
            fullWidth
            label="Session Notes"
            multiline
            rows={4}
            value={sessionNotes}
            onChange={(e) => setSessionNotes(e.target.value)}
            sx={{ mb: 1 }}
            inputProps={{ maxLength: 2000 }}
          />
{/*           <TextField
            margin="normal"
            fullWidth
            label="Customer Notes"
            multiline
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            sx={{ mb: 2 }}
          /> */}
          <Button type="submit" fullWidth variant="contained" sx={{ mt: 1 }}>
            Edit Session
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}

export default EditTaskSessionModal;
